import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  booleanAttribute,
} from '@angular/core';
import {
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  Overlay,
} from '@angular/cdk/overlay';
import { IconName } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'sigecom-popover',
  template: `
    <sigecom-button
      type="default"
      outlined
      [class]="triggerClass"
      [attr.title]="triggerTitle"
      [attr.aria-expanded]="open"
      cdkOverlayOrigin
      #trigger="cdkOverlayOrigin"
      (click)="togglePopover()"
      [icon]="triggerIcon"
      [title]="triggerLabel"
    />


    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="open"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
      [cdkConnectedOverlayPositions]="
        placement === 'left' ? leftOverlayPositions : rightOverlayPositions
      "
      [cdkConnectedOverlayViewportMargin]="8"
      [cdkConnectedOverlayScrollStrategy]="repositionScrollStrategy"
      (backdropClick)="closePopover()"
      (positionChange)="onPositionChange($event)"
    >
      <div class="relative mt-2 z-[130]">
        <div
          class="relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-flat-3xl"
          [ngClass]="panelClass"
        >
          <div
            *ngIf="popoverTitle"
            class="flex items-center justify-between p-2"
          >
            <h5 class="text-xl font-bold text-sigecom-info">
              {{ popoverTitle }}
            </h5>
          </div>

          <div
            [class.pt-5]="popoverTitle"
            (click)="onContentClick()"
          >
            <ng-container
              *ngTemplateOutlet="popoverTemplate; context: context"
            ></ng-container>
          </div>
        </div>
        <div
          *ngIf="panelPosition === 'above'"
          class="pointer-events-none shadow-flat-2xl absolute -bottom-[7px] z-[140] left-5 h-3.5 w-3.5 rotate-45 border-b border-r border-slate-200 bg-white [clip-path:polygon(100%_0,100%_100%,0_100%)]"
        ></div>

        <div
          *ngIf="panelPosition === 'below'"
          class="pointer-events-none shadow-flat-2xl absolute -top-[7px] z-[140] right-5 h-3.5 w-3.5 rotate-45 border-l border-t border-slate-200 bg-white [clip-path:polygon(0_0,100%_0,0_100%)]"
        ></div>
      </div>
    </ng-template>
  `,
})
export class PopoverComponent {
  @Input() popoverTemplate: TemplateRef<any> | null = null;
  @Input() popoverTitle = '';
  @Input() triggerLabel = 'Ações';
  @Input() triggerTitle = 'Ações';
  @Input() triggerIcon: IconName = 'ellipsis-vertical';
  @Input() triggerClass = '';
  @Input() panelClass = 'w-[30rem]';
  @Input() placement: 'left' | 'right' = 'right';
  @Input() context: Record<string, any> = {};
  @Input({ transform: booleanAttribute }) closeOnOptionClick = true;

  @Output() popoverStateChange = new EventEmitter<boolean>();

  open = false;
  panelPosition: 'above' | 'below' = 'below';

  readonly repositionScrollStrategy = this.overlay.scrollStrategies.reposition({
    scrollThrottle: 20,
  });

  readonly leftOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  readonly rightOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  constructor(private readonly overlay: Overlay) { }

  togglePopover(): void {
    this.open = !this.open;
    if (this.open) {
      this.panelPosition = 'below';
    }
    this.popoverStateChange.emit(this.open);
  }

  closePopover(): void {
    if (!this.open) {
      return;
    }

    this.open = false;
    this.popoverStateChange.emit(this.open);
  }

  onContentClick(): void {
    if (this.closeOnOptionClick) {
      this.closePopover();
    }
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    this.panelPosition =
      event.connectionPair.overlayY === 'bottom' ? 'above' : 'below';
  }
}
